import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import type { Difficulty } from '@/types/interview'
import { useEffect, useState } from 'react'

const COMPETENCY_LABELS: Record<string, string> = {
  leadership: 'Leadership',
  teamwork: 'Teamwork',
  communication: 'Communication',
  problem_solving: 'Problem Solving',
  time_management: 'Time Management',
  stakeholder_management: 'Stakeholder Management',
  attention_to_detail: 'Attention to Detail',
}

export interface StarComponentAverages {
  situation: number
  task: number
  action: number
  result: number
  overall: number
}

export interface PracticeTarget {
  type: 'star_component' | 'competency'
  key: string
  label: string
  score: number
}

export interface UseProgressDataReturn {
  starAverages: StarComponentAverages | null
  practiceTargets: PracticeTarget[]
  completedSessionCount: number
  lastSessionRole: string | null
  lastSessionDifficulty: Difficulty | null
  loading: boolean
}

export function useProgressData(): UseProgressDataReturn {
  const { user } = useAuthStore()
  const [starAverages, setStarAverages] = useState<StarComponentAverages | null>(null)
  const [practiceTargets, setPracticeTargets] = useState<PracticeTarget[]>([])
  const [completedSessionCount, setCompletedSessionCount] = useState(0)
  const [lastSessionRole, setLastSessionRole] = useState<string | null>(null)
  const [lastSessionDifficulty, setLastSessionDifficulty] = useState<Difficulty | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) return
    void fetchProgressData()
  }, [user])

  async function fetchProgressData() {
    setLoading(true)

    const { data: sessionRows } = await supabase
      .from('sessions')
      .select('id, target_role, difficulty')
      .eq('status', 'completed')
      .eq('is_demo', false)
      .filter('drill_mode', 'eq', false)
      .order('created_at', { ascending: false })
      .limit(10)

    const sessions = sessionRows ?? []
    setCompletedSessionCount(sessions.length)

    if (sessions.length === 0) {
      setLoading(false)
      return
    }

    const lastSession = sessions[0]
    setLastSessionRole(lastSession?.target_role ?? null)
    setLastSessionDifficulty((lastSession?.difficulty as Difficulty) ?? null)

    const sessionIds = sessions.map((s) => s.id)

    const [{ data: scoreRows }, { data: competencyRows }] = await Promise.all([
      supabase
        .from('scores')
        .select('situation_score, task_score, action_score, result_score, overall_score')
        .in('session_id', sessionIds),
      supabase.from('competency_progress').select('competency, average_score, session_count'),
    ])

    const scores = scoreRows ?? []

    if (scores.length > 0) {
      const n = scores.length
      const avgs: StarComponentAverages = {
        situation: scores.reduce((sum, r) => sum + r.situation_score, 0) / n,
        task: scores.reduce((sum, r) => sum + r.task_score, 0) / n,
        action: scores.reduce((sum, r) => sum + r.action_score, 0) / n,
        result: scores.reduce((sum, r) => sum + r.result_score, 0) / n,
        overall: scores.reduce((sum, r) => sum + r.overall_score, 0) / n,
      }
      setStarAverages(avgs)

      const starComponents: PracticeTarget[] = [
        { type: 'star_component', key: 'situation', label: 'Situation', score: avgs.situation },
        { type: 'star_component', key: 'task', label: 'Task', score: avgs.task },
        { type: 'star_component', key: 'action', label: 'Action', score: avgs.action },
        { type: 'star_component', key: 'result', label: 'Result', score: avgs.result },
      ]
      const weakestStar = starComponents.reduce(
        (min, c) => (c.score < min.score ? c : min),
        // biome-ignore lint/style/noNonNullAssertion: array always has 4 elements
        starComponents[0]!
      )

      const targets: PracticeTarget[] = [weakestStar]

      const eligible = (competencyRows ?? []).filter((r) => r.session_count >= 2)
      if (eligible.length > 0) {
        const weakest = eligible.reduce(
          (min, r) => (r.average_score < min.average_score ? r : min),
          // biome-ignore lint/style/noNonNullAssertion: array filtered to non-empty above
          eligible[0]!
        )
        targets.push({
          type: 'competency',
          key: weakest.competency,
          label: COMPETENCY_LABELS[weakest.competency] ?? weakest.competency,
          score: weakest.average_score,
        })
      }

      setPracticeTargets(targets)
    }

    setLoading(false)
  }

  return {
    starAverages,
    practiceTargets,
    completedSessionCount,
    lastSessionRole,
    lastSessionDifficulty,
    loading,
  }
}
